import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondary,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMicroSitePages } from '../store/actions/pageActions';
import CreatePageDialog from '../components/MicroSite/Page/CreatePageDialog';
import EditPageDialog from '../components/MicroSite/Page/EditPageDialog';
import DeletePageDialog from '../components/MicroSite/Page/DeletePageDialog';
import PageList from '../components/MicroSite/Page/PageList';
import { fetchWithAuth } from '../utils/apiUtils';

function MicroSitePages() {
  const { accountId, siteId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pages, isLoading, error } = useSelector((state) => state.page);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [site, setSite] = useState(null);

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const data = await fetchWithAuth(
          `/api/accounts/${accountId}/microsites/${siteId}`
        );
        setSite(data);
      } catch (error) {
        console.error('Error fetching site:', error);
      }
    };

    if (accountId && siteId) {
      fetchSite();
    }
  }, [accountId, siteId]);

  useEffect(() => {
    if (accountId && siteId) {
      dispatch(fetchMicroSitePages({ accountId, siteId }));
    }
  }, [accountId, siteId, dispatch]);

  const handleEdit = (page) => {
    setSelectedPage(page);
    setIsEditOpen(true);
  };

  const handleDelete = (page) => {
    setSelectedPage(page);
    setIsDeleteOpen(true);
  };

  const handleBack = () => {
    navigate(`/accounts/${accountId}/sites/${siteId}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack}>
          Return to Site Overview
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Site Overview
        </Button>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Pages
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Create New Page
          </Button>
        </Box>
      </Box>

      <PageList
        pages={pages}
        site={site}
        onEditPage={handleEdit}
        onDeletePage={handleDelete}
      />

      <CreatePageDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        accountId={accountId}
        siteId={siteId}
      />

      <EditPageDialog
        open={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedPage(null);
        }}
        accountId={accountId}
        siteId={siteId}
        page={selectedPage}
      />

      <DeletePageDialog
        open={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedPage(null);
        }}
        accountId={accountId}
        siteId={siteId}
        page={selectedPage}
      />
    </Container>
  );
}

export default MicroSitePages; 